import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Document, OntologyStatusEntry } from "@/types/document";

// A chunked job's own roll-up status, mirrored from the backend's
// `_chunk_status`/`_TICKET_JOB_STATUS` vocabulary (app/impact_analysis/service.py).
export type OntologyRunStatus = "pending" | "inprogress" | "done" | "error" | "cancelled";

export interface ImpactAnalysisRunOntology {
  ontologyType: string;
  status: OntologyRunStatus;
}

// Something about a run that did not fail but that the reviewer has to know (breezeai-agents RunWarning):
// "revision_failed" (feedback could not be applied, the previous draft is kept), "left_out" (approved changes the
// write skipped), "uncited" (written nodes without a citation to the document).
export interface RunWarning {
  kind: string;
  message: string;
  failedStep?: string | null;
  items?: string[];
}

export interface ImpactAnalysisRun {
  runId: string;
  runStatus: string;
  document_id: number | null;
  uuid: string;
  type: string;
  agent: string;
  trigger: string;
  // Only on a run fetched from the runs API; absent on rows built from a document's ontologyStatus.
  progress?: { done: number; total: number };
  ontologies: ImpactAnalysisRunOntology[];
  revision: number | null;
  error: string | null;
  failedStep: string | null;
  erroredAt: string | null;
  // Absent from agents that predate it.
  warnings?: RunWarning[];
  // The underlying job row's own `createdAt` — the "is this run stuck"
  // signal for a document whose status is sourced from the run itself
  // instead of `documents.updatedAt`.
  createdAt: string | null;
  // When the run last changed (set by the backend if it exposes it) — the
  // decision time shown for an applied/rejected run.
  updatedAt?: string | null;
}

// The run among a document's runs that carries a given ontology type — each
// job/run entry covers exactly one ontology type, so picking "the functional
// one" (rather than `pickReviewableRun`'s arbitrary newest-or-undecided pick)
// is what the cancelled-vs-error and error-tooltip reads need.
export function pickOntologyRun(
  runs: ImpactAnalysisRun[] | undefined,
  ontologyType: string,
): ImpactAnalysisRun | null {
  if (!runs) return null;
  return runs.find((r) => r.ontologies.some((o) => o.ontologyType === ontologyType)) ?? null;
}

// Runs in these states are done for good (or, for "review", waiting on a
// decision this UI doesn't drive yet); anything else (including "error",
// which can still be retried, and "applying", an approved run still being
// written) is a candidate for the "current run" lookup below.
const TERMINAL_RUN_STATUSES = new Set(["cancelled", "applied", "rejected", "review"]);

// The run Cancel/Retry act on, out of a document's runs (see `runsFromOntologyStatus`): the one non-terminal run,
// or the most recent of any status if every run is terminal (so the call still surfaces a clear error from the run
// endpoint instead of failing locally).
export function pickCurrentRun(runs: ImpactAnalysisRun[]): ImpactAnalysisRun | null {
  return runs.find((r) => !TERMINAL_RUN_STATUSES.has(r.runStatus)) ?? runs[0] ?? null;
}

// Cancel/retry act on a runId. Callers pass the one they have (from the document's `ontologyStatus`); otherwise it is
// read from the document itself, via the documents API, which carries it.
async function resolveRunId(params: {
  projectUuid: string;
  documentId: number | string;
  runId?: string | null;
  token: string;
}): Promise<string> {
  if (params.runId) return params.runId;
  const url =
    `${config.isometricApiUrl}/documents?filters[_id][eq]=${encodeURIComponent(String(params.documentId))}` +
    `&filters[uuid][eq]=${encodeURIComponent(params.projectUuid)}&selectFields=_id,uuid`;
  const response = await apiFetch(url, {
    headers: { Authorization: `Bearer ${params.token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to read the document's run: ${await response.text()}`);
  }
  const doc = ((await response.json()).data ?? [])[0];
  const run = pickCurrentRun(runsFromOntologyStatus(doc));
  if (!run) {
    throw new Error("No impact-analysis run found for this document");
  }
  return run.runId;
}

const OPEN_RUN_STATUSES = new Set(["review", "revising", "applying"]);

// The run a document's review overlay should track: a run still open for a decision ("review", "revising",
// "applying"), so it is never hidden behind a newer unrelated run; otherwise the most recent run (unlike
// `pickCurrentRun`, which treats "review" as out of scope for cancel/retry).
export function pickReviewableRun(runs: ImpactAnalysisRun[]): ImpactAnalysisRun | null {
  // Runs are listed newest-first. Falling back to the first keeps an older run that never finished (e.g. "error")
  // from shadowing a newer applied/rejected one.
  const picked = runs.find((r) => OPEN_RUN_STATUSES.has(r.runStatus)) ?? runs[0] ?? null;
  if (!picked) return null;
  // One row per ontology — the same runId appears once per ontology of a multi-ontology run. Merge them all into one.
  const allOntologies = runs
    .filter((r) => r.runId === picked.runId)
    .flatMap((r) => r.ontologies);
  return { ...picked, ontologies: allOntologies };
}

const JOB_TO_RUN_ONTOLOGY_STATUS: Record<OntologyStatusEntry["status"], OntologyRunStatus> = {
  pending: "pending",
  running: "inprogress",
  done: "done",
  error: "error",
  cancelled: "cancelled",
};

// A document's runs as the list cards read them, built from its `ontologyStatus` (the documents API reads it from
// the run tables): one row per ontology that has a run, newest first. It carries what the list needs (run status,
// run id, agent, error); the review panel loads the full run when it opens.
export function runsFromOntologyStatus(
  doc: Pick<Document, "_id" | "uuid" | "ontologyStatus"> | null | undefined,
): ImpactAnalysisRun[] {
  if (!doc?.ontologyStatus) return [];
  return (Object.entries(doc.ontologyStatus) as [string, OntologyStatusEntry | null][])
    .filter((pair): pair is [string, OntologyStatusEntry] => !!pair[1]?.runId)
    .sort(([, a], [, b]) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .map(([ontologyType, entry]) => ({
      runId: entry.runId!,
      runStatus: entry.runStatus ?? "",
      document_id: Number(doc._id),
      uuid: doc.uuid,
      type: "document",
      agent: entry.agent ?? "",
      trigger: "",
      ontologies: [{ ontologyType, status: JOB_TO_RUN_ONTOLOGY_STATUS[entry.status] ?? "pending" }],
      revision: null,
      error: entry.errorMessage,
      failedStep: entry.failedStep,
      erroredAt: entry.erroredAt,
      warnings: entry.warnings ?? [],
      createdAt: entry.updatedAt,
      updatedAt: entry.updatedAt,
    }));
}

export interface ImpactAnalysisRunChanges {
  runId: string;
  runStatus: string;
  revision: number | null;
  changes: unknown;
}

async function getRunChanges(params: {
  runId: string;
  token: string;
}): Promise<ImpactAnalysisRunChanges> {
  const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/changes`;
  const response = await apiFetch(url, {
    headers: { Authorization: `Bearer ${params.token}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch run changes: ${await response.text()}`);
  }
  return response.json();
}

export function useRunChanges(params: { runId: string | null; enabled?: boolean }) {
  const { getAuthHeaders } = useAuthHeaders();
  const { runId, enabled = true } = params;

  return useQuery({
    queryKey: ["impact-analysis-run-changes", runId],
    enabled: enabled && !!runId,
    queryFn: async (): Promise<ImpactAnalysisRunChanges> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      return getRunChanges({ runId: runId!, token });
    },
  });
}

// Reject has no free-text reason input in the webui today — a fixed reason
// is sent instead.
const REJECT_REASON = "Rejected via webui";

function invalidateRunQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  params: { projectUuid: string; documentId: number | string },
) {
  queryClient.invalidateQueries({
    queryKey: [
      "chunk-progress",
      params.projectUuid,
      "document",
      Number(params.documentId),
      "functional",
    ],
  });
  // The list cards read each run from the documents' `ontologyStatus`.
  queryClient.invalidateQueries({ queryKey: ["document-by-id"] });
}

export function useApproveImpactAnalysisRun() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      runId: string;
      revision: number;
      projectUuid: string;
      documentId: number | string;
    }): Promise<{ runStatus: string }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/approve`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ revision: params.revision }),
      });
      if (!response.ok) {
        // a 409 means the draft moved on (another revision landed): the review screen reloads it
        if (response.status === 409) queryClient.invalidateQueries({ queryKey: ["impact-analysis-run-changes", params.runId] });
        throw new Error(`Failed to approve run: ${await errorDetail(response)}`);
      }
      return response.json();
    },
    onSuccess: (_data, params) => invalidateRunQueries(queryClient, params),
  });
}

export function useRejectImpactAnalysisRun() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      runId: string;
      projectUuid: string;
      documentId: number | string;
    }): Promise<{ runStatus: string }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/reject`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: REJECT_REASON }),
      });
      if (!response.ok) {
        throw new Error(`Failed to reject run: ${await response.text()}`);
      }
      return response.json();
    },
    onSuccess: (_data, params) => invalidateRunQueries(queryClient, params),
  });
}

// A document run is the impact agent (config.agent "ticket"): one queued analysis of every ontology it picks, reviewed
// before anything is written, for documents up to its size limit (a larger one is refused with a 422 naming the limit).
// The old chunked V1 pipeline (agent "ontology") has been removed; its past runs still show their status.

// How the functional analyst looks (config.functional_mode): "standard", or "deep_search" — search hits that name
// every ancestor and the outcome and scenario lists whole, which suits a large graph of many changes. Kept on the run,
// so a retry and a chat revision use the same mode.
export type FunctionalMode = "standard" | "deep_search";

const RUN_AGENT = "ticket";

// The API answers errors as { "error": { "message": "..." } } (or the older { "detail": "..." }); show that sentence
// rather than the raw body.
async function errorDetail(response: Response): Promise<string> {
  return (await errorEnvelope(response)).message;
}

// `fromApi` is false when the body is not the API's own error (a gateway's 502/504 page): the request may still have
// reached the agents service.
async function errorEnvelope(response: Response): Promise<{ message: string; fromApi: boolean }> {
  const text = await response.text();
  try {
    const body = JSON.parse(text);
    const message = body?.error?.message ?? body?.detail;
    if (typeof message === "string") return { message, fromApi: true };
  } catch {
    // not JSON
  }
  return { message: text, fromApi: false };
}

export async function startImpactAnalysisRun(params: {
  projectUuid: string;
  documentId: number | string;
  token: string;
  ontologies?: string[];
  // write the result as soon as the run finishes, with no review (config.skip_approval)
  skipApproval?: boolean;
  // the functional analyst's mode (config.functional_mode); standard when left out
  functionalMode?: FunctionalMode;
  // where the run was started from, for run history: the ontology page (default) or the chat page
  trigger?: "ui" | "chat";
}): Promise<{ runId: string; runStatus: string }> {
  const runConfig = {
    agent: RUN_AGENT,
    ...(params.ontologies ? { ontologies: params.ontologies } : {}),
    ...(params.skipApproval ? { skip_approval: true } : {}),
    // sent only when not the default, like skip_approval
    ...(params.functionalMode === "deep_search" ? { functional_mode: "deep_search" } : {}),
  };
  const response = await apiFetch(`${config.agentsApiUrl}/impact-analysis-runs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
    },
    body: JSON.stringify({
      document_id:
        typeof params.documentId === "string"
          ? Number(params.documentId)
          : params.documentId,
      uuid: params.projectUuid,
      type: "document",
      config: runConfig,
      trigger: params.trigger ?? "ui",
    }),
  });
  if (!response.ok) {
    throw new Error(await errorDetail(response));
  }
  return response.json();
}

export default function useStartImpactAnalysisRun() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: {
      projectUuid: string;
      documentId: number | string;
      ontologies?: string[];
      skipApproval?: boolean;
      functionalMode?: FunctionalMode;
      trigger?: "ui" | "chat";
    }) => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      return startImpactAnalysisRun({ ...params, token });
    },
  });
}

export function useCancelImpactAnalysisRun() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      projectUuid: string;
      documentId: number | string;
      // the run to cancel, when the caller has it (from the document's ontologyStatus)
      runId?: string | null;
    }): Promise<{ runStatus: string }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const runId = await resolveRunId({ ...params, token });
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${runId}/cancel`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to cancel run: ${await response.text()}`);
      }
      return response.json();
    },
    onSuccess: (_data, params) => {
      // Same query key the old cancel/regenerate hooks invalidate — the
      // document's progress badge reads chunk state directly from breezeai-backend,
      // which reflects the run regardless of which endpoint started it.
      queryClient.invalidateQueries({
        queryKey: [
          "chunk-progress",
          params.projectUuid,
          "document",
          Number(params.documentId),
          "functional",
        ],
      });
    },
  });
}

// ── Feedback (chat-driven revision) ──────────────────────────────────────

export interface FeedbackEdit {
  kind: string;
  node: string;
  detail?: string | null;
}

// What a revision changed against the one before it, worked out by the agents (BREEZEAI-1358). `lost` counts nodes
// the revision removed without being asked; they can be put back with useRestoreLostNodes.
export interface RevisionDiff {
  from: number | null;
  to: number | null;
  changed: boolean;
  headline: string[];
  counts: { added: number; removed: number; renamed: number; lost: number };
  carried?: { ontology: string; op: string; label: string; name: string; id: string }[];
}

// One node the last revision removed without being asked, as the run's summary keeps it (`revisionDiff.lost`).
// Its position in that list is what /restore takes.
export interface LostNode {
  ontology: string;
  op: string;
  label: string;
  name: string;
  path?: string;
  id?: string;
}

/** The nodes the last revision lost and can still be restored, from the run's changes (`ontologySummary`). */
export function lostNodesOf(changes: unknown): LostNode[] {
  const diff = (changes as { revisionDiff?: { lost?: unknown } } | null)?.revisionDiff;
  return Array.isArray(diff?.lost) ? (diff.lost as LostNode[]) : [];
}

// One change a revision suggested outside the part the feedback was about (`revisionDiff.proposed`): not in the draft
// until the reviewer accepts it. Its `id` ("p1") is what /proposals takes; `needs` names suggestions that come in with
// it (a new parent).
export interface ProposedChange {
  id: string;
  kind: "add" | "change" | "remove" | string;
  ontology: string;
  label: string;
  name: string;
  path?: string;
  why?: string;
  needs?: string[];
}

/** The suggestions still waiting for the reviewer, from the run's changes (`ontologySummary`). */
export function proposalsOf(changes: unknown): ProposedChange[] {
  const diff = (changes as { revisionDiff?: { proposed?: unknown } } | null)?.revisionDiff;
  return Array.isArray(diff?.proposed) ? (diff.proposed as ProposedChange[]) : [];
}

// A change an answer offered to make ("Shall I apply this?"): applied by sending feedback with `replyTo: id`.
export interface OfferedFix {
  id: string;
  instruction: string;
  severity?: string;
  topic?: string;
}

export interface FeedbackResponse {
  intent: string;
  severity?: string | null;
  topic: string;
  reply: string;
  revision: number;
  edits: FeedbackEdit[];
  changes?: unknown;
  newRunId?: string | null;
  affectedOntologies?: string[] | null;
}

/**
 * Send the feedback POST. The backend runs triage synchronously (~1-2 s)
 * then returns 200 — heavy work (minor/major revise) continues in a
 * background task while the run stays in "revising". The frontend polls
 * GET .../changes until the status flips back to "review".
 *
 * Only a 409 (revision conflict) is surfaced to the user. Network errors
 * and gateway timeouts are swallowed — polling will pick up the result
 * if the backend received the request.
 */
/** Result of the feedback POST — `timedOut` means we need to poll. */
export interface FeedbackMutationResult {
  timedOut: boolean;
  // "major" queues a fresh analysis: the run goes revising → review → running, and lands in review again when done
  severity?: string | null;
}

export function useFeedbackRun() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: async (params: {
      runId: string;
      message: string;
      projectUuid: string;
      documentId: number | string;
      // agree to a change an earlier answer offered (its chat.fix.id)
      replyTo?: string;
    }): Promise<FeedbackMutationResult> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/feedback`;

      let response: Response;
      try {
        response = await apiFetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: params.message, ...(params.replyTo ? { replyTo: params.replyTo } : {}) }),
        });
      } catch {
        // Network error — backend may still have received the request.
        // Polling will pick up the result.
        return { timedOut: true };
      }

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error("Another feedback is in progress. Please wait.");
        }
        // The agents service answered with its own error (e.g. the triage model failed): the run is back in review and
        // nothing was saved, so say so instead of polling for a reply that never comes.
        const { message, fromApi } = await errorEnvelope(response);
        if (fromApi) throw new Error(`Your feedback was not sent: ${message}`);
        // A gateway error — the backend may still be working on it; polling recovers.
        return { timedOut: true };
      }

      // 200 — triaged; a revise carries on in the background
      const body = await response.json().catch(() => null);
      return { timedOut: false, severity: body?.severity ?? null };
    },
  });
}

/**
 * Poll the run-changes endpoint while the run is in "revising" state.
 * Stops automatically once the status flips back to "review" (or any
 * non-revising state). The caller enables this when the feedback POST
 * timed out (returned null) so the UI waits for the backend to finish.
 */
const FEEDBACK_POLL_MS = 3_000;
export const FEEDBACK_IN_FLIGHT = new Set(["revising", "running", "pending"]);

export function useFeedbackPoll(params: {
  runId: string | null;
  enabled: boolean;
}) {
  const { getAuthHeaders } = useAuthHeaders();
  const { runId, enabled } = params;

  return useQuery({
    queryKey: ["feedback-poll", runId],
    enabled: enabled && !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.runStatus;
      // Stop once the revision has landed. A major revision passes through review into a queued re-analysis
      // (running), and only that analysis bumps the revision, so keep polling through it.
      if (status && !FEEDBACK_IN_FLIGHT.has(status)) return false;
      return FEEDBACK_POLL_MS;
    },
    queryFn: async (): Promise<ImpactAnalysisRunChanges> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      return getRunChanges({ runId: runId!, token });
    },
  });
}

// What a chat message did to the draft, one decision each (BREEZEAI-1358): "corrected" only when the draft changed;
// "answered" when the agent only replied; "revision_started" when it queued a re-analysis; "failed" when the revision
// errored. Together they are the run's feedback thread.
export const FEEDBACK_DECISIONS = ["corrected", "answered", "revision_started", "failed"] as const;
export type FeedbackDecisionType = (typeof FEEDBACK_DECISIONS)[number];

// Fetch feedback chat history (ontology_decisions with a FEEDBACK_DECISIONS decision)
export interface FeedbackDecision {
  _id: number;
  runId: string;
  decision: FeedbackDecisionType;
  reason: string | null;
  revision: number | null;
  actor: string | null;
  chat: {
    intent: string;
    topic: string;
    reply: string;
    edits: FeedbackEdit[];
    severity?: string;
    error?: boolean;
    diff?: RevisionDiff;
    // an answer that offers a change: open until a later turn lists its id in appliedFixes
    fix?: OfferedFix;
    canApply?: boolean;
    appliedFixes?: string[];
  } | null;
  createdAt: string;
}

export function useFeedbackHistory(params: { runId: string | null; enabled?: boolean }) {
  const { getAuthHeaders } = useAuthHeaders();
  const { runId, enabled = true } = params;

  return useQuery({
    queryKey: ["feedback-history", runId],
    enabled: enabled && !!runId,
    queryFn: async (): Promise<FeedbackDecision[]> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      // the decision key repeats once per value: the backend reads it as a list for `$in`
      const query = new URLSearchParams([
        ["filters[runId][$eq]", runId!],
        ...FEEDBACK_DECISIONS.map((d) => ["filters[decision][$in]", d]),
        ["sortName", "createdAt"],
        ["sortOrder", "asc"],
        ["limit", "100"],
      ]);
      const url = `${config.isometricApiUrl}/ontology-decisions?${query.toString()}`;
      const response = await apiFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch feedback history: ${await response.text()}`);
      }
      const result = await response.json();
      return result.data ?? [];
    },
  });
}

// Put back nodes the last revision lost, as the next revision: the positions in `items` (into lostNodesOf), or all.
export function useRestoreLostNodes() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: async (params: {
      runId: string;
      items?: number[];
    }): Promise<{ revision: number; restored: number; remaining: number }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/restore`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(params.items ? { items: params.items } : {}),
      });
      if (!response.ok) {
        throw new Error(`Failed to restore: ${await response.text()}`);
      }
      return response.json();
    },
  });
}

// Accept suggestions of the last revision, as the next revision: the ids in `items` (from proposalsOf), or all.
// Done by code on the agents side, so it answers at once — nothing to poll.
export function useApplyProposals() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: async (params: {
      runId: string;
      items?: string[];
    }): Promise<{ revision: number; applied: number; skipped: number; remaining: number }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/proposals`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(params.items ? { items: params.items } : {}),
      });
      if (!response.ok) {
        throw new Error(`Could not accept the suggestions: ${await errorDetail(response)}`);
      }
      return response.json();
    },
  });
}

// Analyse the whole story again, asked for explicitly: a chat correction only edits the part it is about. The run
// goes running → review, so the caller polls like a major revision.
export function useRegenerateRun() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: async (params: { runId: string; feedback?: string }): Promise<{ runId: string; runStatus: string }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${params.runId}/regenerate`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(params.feedback ? { feedback: params.feedback } : {}),
      });
      if (!response.ok) {
        throw new Error(`Could not regenerate: ${await errorDetail(response)}`);
      }
      return response.json();
    },
  });
}

export function useRetryImpactAnalysisRun() {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: async (params: {
      projectUuid: string;
      documentId: number | string;
      // the run to retry, when the caller has it (from the document's ontologyStatus)
      runId?: string | null;
    }): Promise<{ runStatus: string }> => {
      const token = getAuthHeaders().Authorization.replace("Bearer ", "");
      const runId = await resolveRunId({ ...params, token });
      const url = `${config.agentsApiUrl}/impact-analysis-runs/${runId}/retry`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to retry run: ${await response.text()}`);
      }
      return response.json();
    },
  });
}
